import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { searchAPI, userAPI, restaurantAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';

function UserHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [featuredRestaurants, setFeaturedRestaurants] = useState([]);
  const [search, setSearch] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('relevance');
  const [vegFilter, setVegFilter] = useState('all');

  useEffect(() => {
    fetchRestaurants();
    fetchAddresses();
    fetchFeaturedRestaurants();
  }, []);

  // Auto-search as user types - 200ms debounce for performance
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchRestaurants();
    }, 200);

    return () => clearTimeout(delaySearch);
  }, [search, sortBy, vegFilter]);

  const fetchRestaurants = async () => {
    try {
      const params = { q: search };

      // Add sorting parameter
      if (sortBy !== 'relevance') {
        params.sortBy = sortBy;
      }

      // Add veg filter
      if (vegFilter !== 'all') {
        params.vegOnly = vegFilter === 'veg';
      }

      const response = await searchAPI.searchRestaurants(params);
      setRestaurants(response.data.data.restaurants);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await userAPI.getAddresses();
      const addrs = response.data.data.addresses;
      setAddresses(addrs);
      const defaultAddr = addrs.find(a => a.isDefault) || addrs[0];
      setSelectedAddress(defaultAddr);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    }
  };

  const fetchFeaturedRestaurants = async () => {
    try {
      const response = await restaurantAPI.getFeaturedRestaurants({ limit: 6 });
      if (response.data.success) {
        setFeaturedRestaurants(response.data.data.restaurants || []);
      }
    } catch (error) {
      console.error('Failed to fetch featured restaurants:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRestaurants();
  };

  if (loading) return <div className="loading">🔄 Loading delicious food...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Delivery Address Card */}
        <div className="card fade-in" style={{
          marginBottom: 'var(--space-xl)',
          background: 'var(--gradient-primary)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--space-md)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px'
              }}>
                📍
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', opacity: 0.9, fontWeight: '600' }}>
                  DELIVERING TO
                </p>
                <p style={{ margin: 0, fontWeight: '800', fontSize: 'var(--font-size-xl)' }}>
                  {selectedAddress ? `${selectedAddress.label} - ${selectedAddress.city}` : 'Add Address'}
                </p>
              </div>
            </div>
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
                padding: 'var(--space-md) var(--space-lg)',
                background: 'rgba(255, 255, 255, 0.25)',
                borderRadius: 'var(--radius-full)',
                backdropFilter: 'blur(10px)',
                transition: 'all var(--transition-base)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={() => navigate('/user/profile')}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
            >
              <span style={{ fontSize: '24px' }}>👤</span>
              <span style={{ fontWeight: '700' }}>{user?.name}</span>
            </div>
          </div>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            🍽️ Browse Restaurants
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Discover amazing food near you
          </p>
        </div>

        {/* Featured Restaurants Section */}
        {featuredRestaurants.length > 0 && (
          <div className="fade-in" style={{ marginBottom: 'var(--space-2xl)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-md)',
              marginBottom: 'var(--space-lg)'
            }}>
              <span style={{ fontSize: '32px' }}>⭐</span>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                color: 'var(--gray-900)',
                margin: 0
              }}>
                Featured Restaurants
              </h2>
            </div>
            <div style={{
              display: 'flex',
              gap: 'var(--space-lg)',
              overflowX: 'auto',
              paddingBottom: 'var(--space-md)',
              scrollSnapType: 'x mandatory'
            }}>
              {featuredRestaurants.map(restaurant => (
                <Link
                  key={restaurant._id}
                  to={`/user/restaurant/${restaurant._id}`}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    scrollSnapAlign: 'start',
                    flexShrink: 0
                  }}
                >
                  <div className="card" style={{
                    width: '280px',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '2px solid rgba(255, 184, 77, 0.3)',
                    background: 'linear-gradient(135deg, rgba(255, 184, 77, 0.05) 0%, white 100%)'
                  }}>
                    {/* Featured Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'linear-gradient(135deg, #FFB84D 0%, #F59E0B 100%)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: '700',
                      zIndex: 2,
                      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)'
                    }}>
                      ⭐ Featured
                    </div>

                    {/* Restaurant Image */}
                    {restaurant.photos && restaurant.photos.length > 0 && (
                      <div style={{
                        width: '100%',
                        height: '140px',
                        marginBottom: 'var(--space-md)',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                        background: 'var(--gray-100)'
                      }}>
                        <img
                          src={restaurant.photos[0]}
                          alt={restaurant.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}

                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-xs)',
                      color: 'var(--gray-900)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {restaurant.name}
                    </h3>
                    <p style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      marginBottom: 'var(--space-sm)',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {restaurant.cuisineType?.join(', ') || 'Multi-Cuisine'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      <span style={{ fontSize: '16px' }}>⭐</span>
                      <span style={{ fontWeight: '600', color: 'var(--warning)' }}>
                        {restaurant.rating || 'New'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="card fade-in" style={{ marginBottom: 'var(--space-2xl)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap', marginBottom: 'var(--space-md)' }}>
            <input
              type="text"
              placeholder="🔍 Search restaurants, cuisines, or dishes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                minWidth: '300px',
                marginBottom: 0,
                fontSize: 'var(--font-size-lg)'
              }}
            />
            <button type="submit" className="btn btn-primary btn-lg">
              🔎 Search
            </button>
          </div>

          {/* Filters and Sorting */}
          <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <label style={{ fontWeight: '600', color: 'var(--gray-700)', fontSize: 'var(--font-size-sm)' }}>
                Sort by:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: 'var(--space-sm) var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid var(--gray-300)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="relevance">🎯 Relevance</option>
                <option value="rating">⭐ Rating</option>
                <option value="popular">🔥 Most Popular</option>
                <option value="priceLowToHigh">💰 Price: Low to High</option>
                <option value="priceHighToLow">💎 Price: High to Low</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <label style={{ fontWeight: '600', color: 'var(--gray-700)', fontSize: 'var(--font-size-sm)' }}>
                Filter:
              </label>
              <select
                value={vegFilter}
                onChange={(e) => setVegFilter(e.target.value)}
                style={{
                  padding: 'var(--space-sm) var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid var(--gray-300)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="all">🍽️ All</option>
                <option value="veg">🥗 Veg Only</option>
                <option value="nonveg">🍗 Non-Veg</option>
              </select>
            </div>
          </div>
        </form>

        {/* Restaurant Grid */}
        <div className="grid">
          {restaurants.map(restaurant => (
            <Link
              key={restaurant._id}
              to={`/user/restaurant/${restaurant._id}${search ? `?search=${encodeURIComponent(search)}` : ''}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="card fade-in" style={{
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Promoted Badge */}
                {restaurant.isPromoted && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: '700',
                    zIndex: 2,
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)'
                  }}>
                    🚀 Promoted
                  </div>
                )}

                {/* Restaurant Image */}
                {restaurant.photos && restaurant.photos.length > 0 && (
                  <div style={{
                    width: '100%',
                    height: '200px',
                    marginBottom: 'var(--space-md)',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    background: 'var(--gray-100)'
                  }}>
                    <img
                      src={restaurant.photos[0]}
                      alt={restaurant.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Restaurant Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--space-md)'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--gray-900)'
                    }}>
                      {restaurant.name}
                    </h3>
                    <p style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      marginBottom: 'var(--space-md)',
                      lineHeight: '1.5'
                    }}>
                      {restaurant.description}
                    </p>
                  </div>
                  <span className={restaurant.isOpen ? 'badge-success' : 'badge-danger'} style={{
                    padding: 'var(--space-xs) var(--space-md)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: restaurant.isOpen ? 'var(--gradient-success)' : 'var(--danger)',
                    color: 'white'
                  }}>
                    {restaurant.isOpen ? '🟢 Open' : '🔴 Closed'}
                  </span>
                </div>

                {/* Restaurant Details */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-md)',
                  marginTop: 'auto',
                  paddingTop: 'var(--space-md)',
                  borderTop: '2px solid var(--gray-100)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'rgba(78, 205, 196, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(78, 205, 196, 0.2)'
                  }}>
                    <span style={{ fontSize: '18px' }}>🍽️</span>
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', color: 'var(--gray-700)' }}>
                      {restaurant.cuisineType?.join(', ') || 'Multi-Cuisine'}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'rgba(255, 184, 77, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(255, 184, 77, 0.2)'
                  }}>
                    <span style={{ fontSize: '18px' }}>⭐</span>
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: '700', color: 'var(--warning)' }}>
                      {restaurant.rating || 'New'}
                    </span>
                  </div>
                </div>

                {/* Hover Accent */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: 'radial-gradient(circle at top right, rgba(255, 107, 107, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }} />
              </div>
            </Link>
          ))}
        </div>

        {restaurants.length === 0 && (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>🔍</div>
            <h2 style={{ color: 'var(--gray-700)', marginBottom: 'var(--space-md)' }}>
              No restaurants found
            </h2>
            <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-lg)' }}>
              Try adjusting your search terms or browse all restaurants
            </p>
            <button onClick={() => { setSearch(''); fetchRestaurants(); }} className="btn btn-primary">
              🏪 Show All Restaurants
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserHome;
